import '@hotwired/turbo'
import '@hotwired/turbo-rails'
import { encodeMethodIntoRequestBody } from '@hotwired/turbo-rails/app/javascript/turbo/fetch_requests'

import './pricing_toggle'
import './resources_hub'
import './dashboard_activity'
import './dashboard'
import './elements/share_modal'
import './doccu_sidebar'
import './manage_plan_modal'
import './folder_modals'
import './folio_card_modals'
import './settings_modals'
import './public_marketing'
import './folio_detail'

import { createApp, reactive } from 'vue'
import TemplateBuilder from './template_builder/builder'
import ImportList from './template_builder/import_list'
import CohortsList from './cohorts/CohortsList.vue'
import CohortDetail from './cohorts/CohortDetail.vue'
import './cohorts/bulk_signing_portal'
import ProgrammeDetail from './programmes/ProgrammeDetail.vue'

import ToggleVisible from './elements/toggle_visible'
import ToggleCookies from './elements/toggle_cookies'
import DisableHidden from './elements/disable_hidden'
import TurboModal from './elements/turbo_modal'
import FileDropzone from './elements/file_dropzone'
import MenuActive from './elements/menu_active'
import ClipboardCopy from './elements/clipboard_copy'
import DynamicList from './elements/dynamic_list'
import DownloadButton from './elements/download_button'
import SetOriginUrl from './elements/set_origin_url'
import SetTimezone from './elements/set_timezone'
import AutoresizeTextarea from './elements/autoresize_textarea'
import SubmittersAutocomplete from './elements/submitter_autocomplete'
import FolderAutocomplete from './elements/folder_autocomplete'
import SignatureForm from './elements/signature_form'
import InlineSignature from './elements/inline_signature'
import InlineInitials from './elements/inline_initials'
import PasswordForm from './elements/password_form'
import DropdownToggle from './elements/dropdown_toggle'
import SubmitForm from './elements/submit_form'
import PromptPassword from './elements/prompt_password'
import EmailsTextarea from './elements/emails_textarea'
import ToggleSubmit from './elements/toggle_submit'
import ToggleOnSubmit from './elements/toggle_on_submit'
import CheckOnClick from './elements/check_on_click'
import PasswordInput from './elements/password_input'
import SearchInput from './elements/search_input'
import DoccuSearchInput from './elements/doccu_search_input'
import ToggleAttribute from './elements/toggle_attribute'
import LinkedInput from './elements/linked_input'
import CheckboxGroup from './elements/checkbox_group'
import MaskedInput from './elements/masked_input'
import SetDateButton from './elements/set_date_button'
import IndeterminateCheckbox from './elements/indeterminate_checkbox'
import AppTour from './elements/app_tour'
import AppTourStart from './elements/app_tour_start'
import DashboardDropzone from './elements/dashboard_dropzone'
import RequiredCheckboxGroup from './elements/required_checkbox_group'
import PageContainer from './elements/page_container'
import EmailEditor from './elements/email_editor'
import MountOnClick from './elements/mount_on_click'
import RemoveOnEvent from './elements/remove_on_event'
import ScrollTo from './elements/scroll_to'
import ScrollLogo from './elements/scroll_logo'
import CustomCursor from './elements/custom_cursor'
import NewsletterForm from './elements/newsletter_form'
import MaintenanceBanner from './elements/maintenance_banner'

import RegistrationForm from './elements/registration_form'
import SetValue from './elements/set_value'
import ReviewForm from './elements/review_form'
import ShowOnValue from './elements/show_on_value'
import CustomValidation from './elements/custom_validation'
import ToggleClasses from './elements/toggle_classes'
import AutosizeField from './elements/autosize_field'
import BulkUploadForm from './elements/bulk_upload_form'
import DeleteAccountModal from './elements/delete_account_modal'
import WelcomeModal from './elements/welcome_modal'
import WhatsNewModal from './elements/whats_new_modal'
import OnboardingTour from './elements/onboarding_tour'
import OnboardingVideoModal from './elements/onboarding_video_modal'
import GoogleDriveFilePicker from './elements/google_drive_file_picker'
import OpenModal from './elements/open_modal'
import BarChart from './elements/bar_chart'
import FieldCondition from './elements/field_condition'
import SubscriptionGate from './elements/subscription_gate'
import TeleportDropdown from './elements/teleport_dropdown'
import CommandCentreViewer from './elements/command_centre_viewer'
import DispatchEvent from './elements/dispatch_event'
import './elements/add_cohort_wizard'

import * as TurboInstantClick from './lib/turbo_instant_click'
import './elements/upload_feedback'
import { initAsyncButtonMutex } from './lib/async_button_mutex'

import NotificationWidget from './components/notifications/NotificationWidget.vue'
import NotificationsPage from './components/notifications/NotificationsPage.vue'
import MobileHeaderMenu from './components/header/MobileHeaderMenu.vue'

TurboInstantClick.start()
initAsyncButtonMutex()

// Skip ngrok free tier browser warning for Turbo and window.fetch when running via ngrok
const isNgrok = typeof window !== 'undefined' && window.location?.hostname?.includes('ngrok')
if (isNgrok) {
  document.addEventListener('turbo:before-fetch-request', (event) => {
    event.detail.fetchOptions.headers['ngrok-skip-browser-warning'] = 'true'
  })

  if (window.fetch) {
    const _origFetch = window.fetch
    window.fetch = function (resource, init = {}) {
      init = init || {}
      const headers = new Headers(init.headers || {})
      if (!headers.has('ngrok-skip-browser-warning')) {
        headers.set('ngrok-skip-browser-warning', 'true')
      }
      init.headers = headers
      return _origFetch.call(this, resource, init)
    }
  }
}

document.addEventListener('turbo:before-cache', () => {
  window.flash?.remove()
})

document.addEventListener('click', (event) => {
  if (event.target.closest('[data-action="print-policy"]')) {
    event.preventDefault()
    window.print()
  }
})

function escapeHtml (str) {
  if (!str) return ''
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

window.showFlash = function (type, message) {
  let flashContainer = document.getElementById('flash')
  if (!flashContainer) {
    flashContainer = document.createElement('div')
    flashContainer.id = 'flash'
    flashContainer.className = 'fixed top-6 right-6 w-full max-w-[380px] z-[9999] transform transition-all duration-300 flex flex-col gap-[10px]'
    document.body.appendChild(flashContainer)
  }
  const id = 'flash-' + Date.now()
  const isSuccess = type === 'success' || type === 'notice'
  const wrapper = document.createElement('div')
  wrapper.id = id
  wrapper.className = `mx-4 md:mx-0 px-5 py-4 rounded-[16px] shadow-[0_8px_30px_rgb(0,0,0,0.12)] flex items-start justify-between border ${isSuccess ? 'bg-white border-[#01d680]' : 'bg-[#fffcfc] border-[#ff4d4f]'} relative`

  wrapper.innerHTML = `
    <div class="flex items-start gap-[14px]">
      ${isSuccess
        ? '<svg class="w-[22px] h-[22px] text-[#01d680] shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>'
        : '<svg class="w-[22px] h-[22px] text-[#ff4d4f] shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>'
      }
      <div class="flex flex-col pr-[20px]">
        <span class="font-['Raleway'] font-bold text-[15px] leading-snug ${isSuccess ? 'text-[#043f42]' : 'text-[#cf1322]'}">${isSuccess ? 'Success' : 'Error'}</span>
        <span class="font-['Raleway'] text-[13.5px] text-[#555555] mt-[2px] leading-tight">${escapeHtml(message)}</span>
      </div>
    </div>
    <remove-on-event data-event-type="click" data-selector-id="${id}" class="absolute top-[16px] right-[16px] text-[#999] hover:text-[#333] cursor-pointer text-[20px] leading-none transition-colors flex items-center justify-center w-[20px] h-[20px]">&times;</remove-on-event>
  `
  flashContainer.appendChild(wrapper)

  setTimeout(() => {
    if (document.getElementById(id)) {
      document.getElementById(id).remove()
    }
  }, 5000)
}

document.addEventListener('keyup', (e) => {
  if (e.code === 'Escape') {
    document.activeElement?.blur()
  }
})

document.addEventListener('turbo:before-fetch-request', encodeMethodIntoRequestBody)
document.addEventListener('turbo:before-fetch-request', (event) => {
  event.detail.fetchOptions.headers['X-Turbo'] = 'true'
})
document.addEventListener('turbo:submit-end', async (event) => {
  const resp = event.detail?.formSubmission?.result?.fetchResponse?.response

  if (!resp?.headers?.get('content-disposition')?.includes('attachment')) {
    return
  }

  const url = URL.createObjectURL(await resp.blob())
  const link = document.createElement('a')

  link.href = url
  link.setAttribute('download', decodeURIComponent(resp.headers.get('content-disposition').split('"')[1]))

  document.body.appendChild(link)

  link.click()

  document.body.removeChild(link)

  URL.revokeObjectURL(url)
})

const safeRegisterElement = (name, element, options = {}) => {
  try {
    if (element && typeof element === 'function' && !window.customElements.get(name)) {
      window.customElements.define(name, element, options)
    }
  } catch (err) {
    console.error(`Failed to register custom element <${name}>:`, err)
  }
}

safeRegisterElement('toggle-visible', ToggleVisible)
safeRegisterElement('disable-hidden', DisableHidden)
safeRegisterElement('turbo-modal', TurboModal)
safeRegisterElement('file-dropzone', FileDropzone)
safeRegisterElement('menu-active', MenuActive)
safeRegisterElement('clipboard-copy', ClipboardCopy)
safeRegisterElement('dynamic-list', DynamicList)
safeRegisterElement('download-button', DownloadButton)
safeRegisterElement('set-origin-url', SetOriginUrl)
safeRegisterElement('set-timezone', SetTimezone)
safeRegisterElement('autoresize-textarea', AutoresizeTextarea)
safeRegisterElement('submitters-autocomplete', SubmittersAutocomplete)
safeRegisterElement('folder-autocomplete', FolderAutocomplete)
safeRegisterElement('signature-form', SignatureForm)
safeRegisterElement('inline-signature', InlineSignature)
safeRegisterElement('inline-initials', InlineInitials)
safeRegisterElement('password-form', PasswordForm)
safeRegisterElement('dropdown-toggle', DropdownToggle)
safeRegisterElement('submit-form', SubmitForm)
safeRegisterElement('prompt-password', PromptPassword)
safeRegisterElement('emails-textarea', EmailsTextarea)
safeRegisterElement('toggle-cookies', ToggleCookies)
safeRegisterElement('toggle-submit', ToggleSubmit)
safeRegisterElement('toggle-on-submit', ToggleOnSubmit)
safeRegisterElement('password-input', PasswordInput)
safeRegisterElement('search-input', SearchInput)
safeRegisterElement('doccu-search-input', DoccuSearchInput)
safeRegisterElement('toggle-attribute', ToggleAttribute)
safeRegisterElement('linked-input', LinkedInput)
safeRegisterElement('checkbox-group', CheckboxGroup)
safeRegisterElement('masked-input', MaskedInput)
safeRegisterElement('set-date-button', SetDateButton)
safeRegisterElement('indeterminate-checkbox', IndeterminateCheckbox)
safeRegisterElement('app-tour', AppTour)
safeRegisterElement('app-tour-start', AppTourStart)
safeRegisterElement('dashboard-dropzone', DashboardDropzone)
safeRegisterElement('check-on-click', CheckOnClick)
safeRegisterElement('required-checkbox-group', RequiredCheckboxGroup)
safeRegisterElement('page-container', PageContainer)
safeRegisterElement('email-editor', EmailEditor)
safeRegisterElement('mount-on-click', MountOnClick)
safeRegisterElement('remove-on-event', RemoveOnEvent)
safeRegisterElement('scroll-to', ScrollTo)
safeRegisterElement('scroll-logo', ScrollLogo)
safeRegisterElement('custom-cursor', CustomCursor)
safeRegisterElement('newsletter-form', NewsletterForm)
safeRegisterElement('doccu-maintenance-banner', MaintenanceBanner)
safeRegisterElement('registration-form', RegistrationForm)
safeRegisterElement('set-value', SetValue)
safeRegisterElement('review-form', ReviewForm)
safeRegisterElement('show-on-value', ShowOnValue)
safeRegisterElement('custom-validation', CustomValidation)
safeRegisterElement('toggle-classes', ToggleClasses)
safeRegisterElement('autosize-field', AutosizeField)
safeRegisterElement('google-drive-file-picker', GoogleDriveFilePicker)
safeRegisterElement('open-modal', OpenModal)
safeRegisterElement('bar-chart', BarChart)
safeRegisterElement('field-condition', FieldCondition)
safeRegisterElement('bulk-upload-form', BulkUploadForm)
safeRegisterElement('delete-account-modal', DeleteAccountModal)
safeRegisterElement('welcome-modal', WelcomeModal)
safeRegisterElement('whats-new-modal', WhatsNewModal)
safeRegisterElement('onboarding-tour', OnboardingTour)
safeRegisterElement('onboarding-video-modal', OnboardingVideoModal)
safeRegisterElement('subscription-gate', SubscriptionGate)
safeRegisterElement('teleport-dropdown', TeleportDropdown)
safeRegisterElement('command-centre-viewer', CommandCentreViewer)
safeRegisterElement('dispatch-event', DispatchEvent)

safeRegisterElement('notification-widget', class extends HTMLElement {
  connectedCallback () {
    this.appElem = document.createElement('div')
    this.app = createApp(NotificationWidget)
    this.app.mount(this.appElem)
    this.appendChild(this.appElem)
  }

  disconnectedCallback () {
    this.app?.unmount()
    this.appElem?.remove()
  }
})

safeRegisterElement('mobile-header-menu', class extends HTMLElement {
  connectedCallback () {
    this.appElem = document.createElement('div')
    this.app = createApp(MobileHeaderMenu, {
      userJson: this.dataset.user
    })
    this.app.mount(this.appElem)
    this.appendChild(this.appElem)
  }

  disconnectedCallback () {
    this.app?.unmount()
    this.appElem?.remove()
  }
})

safeRegisterElement('notifications-page', class extends HTMLElement {
  connectedCallback () {
    this.appElem = document.createElement('div')
    this.appElem.className = 'w-full h-full flex flex-col min-h-0'
    this.app = createApp(NotificationsPage)
    this.app.mount(this.appElem)
    this.appendChild(this.appElem)
  }

  disconnectedCallback () {
    this.app?.unmount()
    this.appElem?.remove()
  }
})

safeRegisterElement('template-builder', class extends HTMLElement {
  connectedCallback () {
    document.addEventListener('turbo:submit-end', this.onSubmit)

    this.appElem = document.createElement('div')

    this.appElem.classList.add('md:h-screen')

    this.app = createApp(TemplateBuilder, {
      template: reactive(this.dataset.template ? JSON.parse(this.dataset.template) : {}),
      backgroundColor: '#f5fefe',
      locale: this.dataset.locale,
      withPhone: this.dataset.withPhone === 'true',
      withVerification: ['true', 'false'].includes(this.dataset.withVerification) ? this.dataset.withVerification === 'true' : null,
      withKba: ['true', 'false'].includes(this.dataset.withKba) ? this.dataset.withKba === 'true' : null,
      withLogo: this.dataset.withLogo !== 'false',
      withFieldsDetection: this.dataset.withFieldsDetection === 'true',
      editable: this.dataset.editable !== 'false',
      autosave: this.dataset.autosave !== 'false',
      authenticityToken: document.querySelector('meta[name="csrf-token"]')?.content,
      withPayment: this.dataset.withPayment === 'true',
      isPaymentConnected: this.dataset.isPaymentConnected === 'true',
      withFormula: this.dataset.withFormula === 'true',
      withSendButton: this.dataset.withSendButton !== 'false',
      isDocument: this.dataset.isDocument === 'true',
      withSignYourselfButton: this.dataset.withSignYourselfButton !== 'false',
      signYourselfText: this.dataset.signYourselfText,
      withConditions: this.dataset.withConditions === 'true',
      withGoogleDrive: this.dataset.withGoogleDrive === 'true',
      onlyDefinedFields: this.dataset.onlyDefinedFields === 'true',
      withReplaceAndCloneUpload: true,
      withDownload: this.dataset.withDownload !== 'false',
      withPreferences: this.dataset.withPreferences !== 'false',
      currencies: (this.dataset.currencies || '').split(',').filter(Boolean),
      acceptFileTypes: this.dataset.acceptFileTypes,
      showTourStartForm: this.dataset.showTourStartForm === 'true',
      programmes: this.dataset.programmes ? JSON.parse(this.dataset.programmes) : [],
      fieldTypes: this.dataset.fieldTypes ? JSON.parse(this.dataset.fieldTypes) : [],
      defaultSubmitters: this.dataset.defaultSubmitters ? JSON.parse(this.dataset.defaultSubmitters) : [],
      cohortId: this.dataset.cohortId ? parseInt(this.dataset.cohortId) : null,
      programmeId: this.dataset.programmeId ? parseInt(this.dataset.programmeId) : null
    })

    this.component = this.app.mount(this.appElem)

    this.appendChild(this.appElem)
  }

  onSubmit = (e) => {
    if (e.detail.success && e.detail?.formSubmission?.formElement?.id === 'submitters_form') {
      e.detail.fetchResponse.response.json().then((data) => {
        this.component.template.submitters = data.submitters
      })
    }
  }

  disconnectedCallback () {
    document.removeEventListener('turbo:submit-end', this.onSubmit)

    this.app?.unmount()
    this.appElem?.remove()
  }
})

safeRegisterElement('import-list', class extends HTMLElement {
  connectedCallback () {
    this.appElem = document.createElement('div')

    this.app = createApp(ImportList, {
      template: this.dataset.template ? JSON.parse(this.dataset.template) : {},
      multitenant: this.dataset.multitenant === 'true',
      authenticityToken: document.querySelector('meta[name="csrf-token"]')?.content,
      i18n: JSON.parse(this.dataset.i18n || '{}')
    })

    this.app.mount(this.appElem)

    this.appendChild(this.appElem)
  }

  disconnectedCallback () {
    this.app?.unmount()
    this.appElem?.remove()
  }
})

safeRegisterElement('cohorts-list', class extends HTMLElement {
  connectedCallback () {
    this.appElem = document.createElement('div')
    this.appElem.className = 'w-full h-full flex flex-col min-h-0 flex-1'

    this.app = createApp(CohortsList, {
      cohorts: JSON.parse(this.dataset.cohorts || '[]'),
      templates: JSON.parse(this.dataset.templates || '[]'),
      activeTab: this.dataset.activeTab || 'cohorts',
      currentFilter: this.dataset.currentFilter,
      query: this.dataset.query,
      canCreate: this.dataset.canCreate === 'true',
      canCreateCohortTemplate: this.dataset.canCreateCohortTemplate === 'true',
      totalCount: parseInt(this.dataset.totalCount || '0'),
      pagy: this.dataset.pagy ? JSON.parse(this.dataset.pagy) : null,
      currentUserId: this.dataset.currentUserId ? parseInt(this.dataset.currentUserId) : null
    })

    this.app.mount(this.appElem)
    this.appendChild(this.appElem)
  }

  disconnectedCallback () {
    this.app?.unmount()
    this.appElem?.remove()
  }
})

safeRegisterElement('cohort-detail', class extends HTMLElement {
  connectedCallback () {
    this.appElem = document.createElement('div')
    this.appElem.className = 'w-full h-full flex flex-col min-h-0 flex-1'

    this.app = createApp(CohortDetail, {
      cohort: JSON.parse(this.dataset.cohort || '{}'),
      initialFilter: this.dataset.initialFilter || 'all'
    })

    this.app.mount(this.appElem)
    this.appendChild(this.appElem)
  }

  disconnectedCallback () {
    this.app?.unmount()
    this.appElem?.remove()
  }
})

safeRegisterElement('programme-detail', class extends HTMLElement {
  connectedCallback () {
    this.appElem = document.createElement('div')
    this.appElem.className = 'w-full h-full flex flex-col min-h-0 flex-1'

    let programmeData = {}
    let enrollmentsData = []
    try {
      programmeData = JSON.parse(this.dataset.programme || '{}')
      enrollmentsData = JSON.parse(this.dataset.enrollments || '[]')
    } catch (e) {
      console.error('Failed to parse programme data', e)
    }

    this.app = createApp(ProgrammeDetail, {
      programme: programmeData,
      enrollments: enrollmentsData,
      freeTier: this.dataset.freeTier === 'true',
      zipExportInProgress: this.dataset.zipExportInProgress === 'true',
      accountId: this.dataset.accountId
    })

    this.app.mount(this.appElem)
    this.appendChild(this.appElem)
  }

  disconnectedCallback () {
    this.app?.unmount()
    this.appElem?.remove()
  }
})
